import React, { createContext, useContext, useRef, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { WebSocketService, MessageHandler, WebSocketMessage } from '../services/WebSocket';
import logger from '../logging/logger';

interface WebSocketContextType {
  connectToSession: (sessionId: string) => Promise<void>;
  disconnectFromSession: (sessionId: string) => void;
  sendMessage: (sessionId: string, content: string, messageId?: string) => void;
  stopGeneration: (sessionId: string, messageId?: string) => void;
  subscribeToSession: (sessionId: string, handlers: SessionMessageHandlers) => () => void;
  isSessionConnected: (sessionId: string) => boolean;
}

interface SessionMessageHandlers {
  onMessageStart?: (data: any) => void;
  onContentDelta?: (data: any) => void;
  onMessageComplete?: (data: any) => void;
  onGenerationStopped?: (data: any) => void;
  onSessionBusy?: (data: any) => void;
  onSessionReady?: (data: any) => void;
  onSessionUpdated?: (data: any) => void;
  onChartData?: (data: any) => void;
  onBacktestConfig?: (data: any) => void;
  onManualOrderDraft?: (data: any) => void;
  onAgentActivity?: (data: any) => void;
  onError?: (data: any) => void;
  onRateLimitError?: (data: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { isAuthenticated, getToken } = useAuth();

  const sessionConnections = useRef<Map<string, WebSocketService>>(new Map());
  const sessionHandlers = useRef<Map<string, SessionMessageHandlers>>(new Map());
  const currentSessionId = useRef<string | null>(null);

  // NEW: queue messages until socket is open
  const pendingSends = useRef<Map<string, WebSocketMessage[]>>(new Map());

  const flushPending = (sessionId: string) => {
    const wsService = sessionConnections.current.get(sessionId);
    if (!wsService?.isConnected()) return;

    const q = pendingSends.current.get(sessionId);
    if (!q || q.length === 0) return;

    logger.info(`📤 Flushing ${q.length} queued message(s) for session:`, sessionId);
    while (q.length) {
      const m = q.shift()!;
      try {
        wsService.sendMessage(m);
      } catch (e) {
        logger.error('❌ Failed sending queued message:', e);
        // put it back at the front and stop flushing
        q.unshift(m);
        break;
      }
    }

    if (q.length === 0) pendingSends.current.delete(sessionId);
  };

  const disconnectFromSession = useCallback((sessionId: string) => {
    const wsService = sessionConnections.current.get(sessionId);
    if (wsService) {
      wsService.disconnect();
      sessionConnections.current.delete(sessionId);
      sessionHandlers.current.delete(sessionId);
      pendingSends.current.delete(sessionId);

      if (currentSessionId.current === sessionId) currentSessionId.current = null;
    }
  }, []);

  const connectToSession = useCallback(async (sessionId: string) => {
    if (!isAuthenticated) throw new Error('User not authenticated');

    // Only disconnect previous session if it's a DIFFERENT session
    // Don't track currentSessionId here - let each page manage its own subscription
    if (currentSessionId.current && currentSessionId.current !== sessionId) {
      logger.info('🔄 Switching sessions, closing previous:', currentSessionId.current);
      disconnectFromSession(currentSessionId.current);
    }
    currentSessionId.current = sessionId;

    const existing = sessionConnections.current.get(sessionId);
    if (existing?.isConnected()) {
      logger.info('✅ Already connected to session:', sessionId);
      // IMPORTANT: Still notify new subscribers that session is ready
      // This handles the case where NewChatPage -> SessionChatPage transition
      // reuses the same WebSocket connection
      setTimeout(() => {
        sessionHandlers.current.get(sessionId)?.onSessionReady?.({
          type: 'session_ready',
          session_id: sessionId,
          source: 'existing_connection',
        });
      }, 0);
      return;
    }

    logger.info('🔌 Connecting to session:', sessionId);

    const token = await getToken();
    if (!token) throw new Error('Failed to get authentication token');

    const wsService = new WebSocketService();

    const messageHandler: MessageHandler = {
      onConnectionOpen: () => {
        logger.info('🌐 Connected to session:', sessionId);

        // Treat open as ready (covers backends that don't emit session_ready)
        sessionHandlers.current.get(sessionId)?.onSessionReady?.({
          type: 'session_ready',
          session_id: sessionId,
          source: 'client_onopen',
        });

        // Flush any queued sends
        flushPending(sessionId);
      },
      onConnectionClose: (event) => {
        logger.info('🌐 Disconnected from session:', sessionId, 'code:', event.code, 'wasClean:', event.wasClean);
        sessionConnections.current.delete(sessionId);

        // Only notify error handler for unexpected disconnections (not clean client-initiated closes)
        // Code 1000 = normal closure, code 1001 = going away (tab close)
        if (event.code !== 1000 && event.code !== 1001) {
          sessionHandlers.current.get(sessionId)?.onError?.({
            type: 'error',
            message: 'WebSocket connection closed unexpectedly',
            session_id: sessionId,
            is_disconnect: true,
          });
        }
      },
      onConnectionError: (error) => {
        logger.error('🌐 Session error:', sessionId, error);
      },
      onMessageStart: (data) => sessionHandlers.current.get(sessionId)?.onMessageStart?.(data),
      onContentDelta: (data) => sessionHandlers.current.get(sessionId)?.onContentDelta?.(data),
      onMessageComplete: (data) => sessionHandlers.current.get(sessionId)?.onMessageComplete?.(data),
      onGenerationStopped: (data) => sessionHandlers.current.get(sessionId)?.onGenerationStopped?.(data),
      onSessionBusy: (data) => sessionHandlers.current.get(sessionId)?.onSessionBusy?.(data),
      onSessionReady: (data) => sessionHandlers.current.get(sessionId)?.onSessionReady?.(data),
      onSessionUpdated: (data) => sessionHandlers.current.get(sessionId)?.onSessionUpdated?.(data),
      onChartData: (data) => sessionHandlers.current.get(sessionId)?.onChartData?.(data),
      onBacktestConfig: (data) => {
        sessionHandlers.current.get(sessionId)?.onBacktestConfig?.(data);
      },
      onManualOrderDraft: (data) => {
        sessionHandlers.current.get(sessionId)?.onManualOrderDraft?.(data);
      },
      onAgentActivity: (data) => sessionHandlers.current.get(sessionId)?.onAgentActivity?.(data),
      onError: (data) => sessionHandlers.current.get(sessionId)?.onError?.(data),
      onRateLimitError: (data) => sessionHandlers.current.get(sessionId)?.onRateLimitError?.(data),
    };

    wsService.connect(sessionId, token, messageHandler);
    sessionConnections.current.set(sessionId, wsService);
  }, [isAuthenticated, getToken, disconnectFromSession]);

  const sendMessage = useCallback((sessionId: string, content: string, messageId?: string) => {
    const wsService = sessionConnections.current.get(sessionId);

    const payload: WebSocketMessage = {
      type: 'chat_message',
      content,
      message_id: messageId,
    };

    if (wsService?.isConnected()) {
      wsService.sendMessage(payload);
      return;
    }

    // NEW: enqueue + connect
    logger.info('🕒 WS not connected yet - queueing message and connecting:', sessionId);
    const q = pendingSends.current.get(sessionId) ?? [];
    q.push(payload);
    pendingSends.current.set(sessionId, q);

    // fire-and-forget connect
    connectToSession(sessionId).catch((e) => {
      logger.error('❌ Failed to connect while sending:', e);
    });
  }, [connectToSession]);

  const stopGeneration = useCallback((sessionId: string, messageId?: string) => {
    logger.info('🛑 stopGeneration called for session:', sessionId);
    const wsService = sessionConnections.current.get(sessionId);

    const stopMessage: WebSocketMessage = {
      type: 'stop_generation',
      session_id: sessionId,
      message_id: messageId || 'stop_request',
    };

    if (wsService?.isConnected()) {
      try {
        wsService.sendMessage(stopMessage);
      } catch (e) {
        logger.error('❌ Failed to send stop message:', e);
        // Notify handlers that stop failed so UI can reset state
        sessionHandlers.current.get(sessionId)?.onGenerationStopped?.({
          type: 'generation_stopped',
          session_id: sessionId,
          reason: 'stop_failed_locally',
        });
      }
    } else {
      logger.error('❌ WebSocket not connected for session:', sessionId);
      // Even if WS not connected, still notify UI to reset state
      // This prevents UI from being stuck in processing state
      sessionHandlers.current.get(sessionId)?.onGenerationStopped?.({
        type: 'generation_stopped',
        session_id: sessionId,
        reason: 'ws_not_connected',
      });
    }
  }, []);

  const subscribeToSession = useCallback((sessionId: string, handlers: SessionMessageHandlers) => {
      logger.info('📋 Subscribing to session:', sessionId);

      // IMPORTANT: allow handler refresh (don't ignore if already subscribed)
      sessionHandlers.current.set(sessionId, handlers);

      // Auto-connect
      connectToSession(sessionId).catch((e) => logger.error('❌ connectToSession failed:', e));

      return () => {
        logger.info('🗑️ Unsubscribing from session:', sessionId);
        sessionHandlers.current.delete(sessionId);
        // Disconnect the WebSocket so the backend cleans up the connection.
        // Without this, switching chats (A → B → A) creates a new WS for A
        // while the backend may still broadcast to it as a "different client",
        // causing the spurious "new message" refresh banner.
        disconnectFromSession(sessionId);
      };
    }, [connectToSession, disconnectFromSession]);

  const isSessionConnected = useCallback((sessionId: string): boolean => {
    const wsService = sessionConnections.current.get(sessionId);
    return wsService?.isConnected() || false;
  }, []);

  // Cleanup on page unload only (not on component unmount)
  // This is important because React StrictMode double-mounts components in development,
  // and we don't want to disconnect WebSockets during the StrictMode unmount/remount cycle
  useEffect(() => {
    const handleBeforeUnload = () => {
      logger.info('🔌 Page unloading, disconnecting all WebSockets');
      sessionConnections.current.forEach((wsService) => wsService.disconnect());
      sessionConnections.current.clear();
      sessionHandlers.current.clear();
      pendingSends.current.clear();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // NOTE: We intentionally do NOT call handleBeforeUnload() here
      // because React StrictMode will unmount/remount this component in development,
      // and we don't want to disconnect WebSockets during that cycle.
      // The WebSocket connections will be cleaned up when the page actually unloads.
    };
  }, []);

  const value = useMemo<WebSocketContextType>(() => ({
    connectToSession,
    disconnectFromSession,
    sendMessage,
    stopGeneration,
    subscribeToSession,
    isSessionConnected,
  }), [connectToSession, disconnectFromSession, sendMessage, stopGeneration, subscribeToSession, isSessionConnected]);

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) throw new Error('useWebSocket must be used within a WebSocketProvider');
  return context;
};
